import { MassUserValidationService } from './mass-upload.validator';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomValidationService } from 'src/app/shared/services/validator/custom-validation.service';
import { SupplierFileValidationEnum } from 'src/app/shared/enum/supplier-file-validation.enum';
import { Observable } from 'rxjs';
import { HcPagination } from 'src/app/modals/hc-pagination.modal';
import * as _ from 'lodash';
import { RegexEnum } from 'src/app/core/enum/regex.enum';

@Injectable({
  providedIn: 'root'
})
export class UserMassUploadService {

  public userApiUrl = environment.runtime.userManagementApiUrl;
  private _uploadedFile: File;

  /**
   * BatchId for current processing group in temp table
   */
  private _batchId: string;

  /**
   * Count for total suppliers in file
   */
  private _totalUsersCount: number;


  constructor(
    private _httpClient: HttpClient,
    private _fb: FormBuilder,
    public _customValitor: MassUserValidationService
    ) { }

  public setUploadedFile(file: File): void {
    this._uploadedFile = file;
  }

  public setTotalUsers(totalUsers: number): void {
    this._totalUsersCount = totalUsers;
  }

  public getTotalUsersCount(): number {
    return this._totalUsersCount;
  }

  public setBatchId(batchId: string): void {
    this._batchId = batchId;
  }

  /**
   * Check if file has expected extension and size(<5MB)
   */
  public validateFileHeader(): any {
    if (!this._uploadedFile) throw Error('File Not found.');

    const fileNameArr = this._uploadedFile.name.split('.');
    const type = fileNameArr[fileNameArr.length - 1];

    let result = { valid: true, message: '' };
    if (!this._uploadedFile) {
      result.valid = false;
      result.message = 'Error while loading file.';
    } else if (SupplierFileValidationEnum.SUPPORTED_FILE_TYPES.split(',').indexOf(type.toUpperCase()) === -1) {
      result.valid = false;
      result.message = 'File type doesn’t match. Please reupload with the correct template.';
    } else if (this._uploadedFile.size > Number(SupplierFileValidationEnum.MAX_FILE_SIZE)) {
      result.valid = false;
      result.message = 'File size exceeded.';
    }

    return result;
  }


  /**
   * Upload excel file to server and get back batchID
   */
  public validateUsers(): Observable<{}> {
    const data = new FormData();
    data.append('file', this._uploadedFile);
    return this._httpClient.post(`${this.userApiUrl}/user/mass-upload`, data);
  }

  /**
   * Get temp suppliers
   * @param page
   * @param status
   */
  public getUsers(page: HcPagination, status: string = 'PROCESSING'): Observable<{}> {
    if (!this._batchId) throw Error('BatchId not found');
    return this._httpClient.get(`${this.userApiUrl}/user/mass-upload/temp-users/${this._batchId}?page=${page.getPageIndex()}&&pageSize=${page.getPageSize()}&&status=${status}`);
  }

  /**
   * Update User information in temporary collection
   * @param user
   */
  public updateUser(user: any, id: string): Observable<{}> {
    return this._httpClient.put(`${this.userApiUrl}/user/mass-upload/${id}`, user);
  }

  /**
   * Create valid users from temp collection
   */
  public submitValidUsers(): Observable<{}> {
    return this._httpClient.post(`${this.userApiUrl}/user/mass-upload/save/${this._batchId}`, {});
  }

  /**
   * Download invalid users into excel sheet
   */
  public getDownloadInvalidUserLink(): Observable<Blob> {
    // This route returns an excel file to download. We wrap it in _httpClient and specify the return type as a blob. See review-users.compontent.ts to see how to unpack and save the file.
    return this._httpClient.get(`${this.userApiUrl}/user/mass-upload/download-invalid-users/${this._batchId}`, {responseType: 'blob'});
  }

  public checkExistingUser(emailToCheck): Observable<{}> {
    return this._httpClient.get(`${this.userApiUrl}/user/mass-upload/${emailToCheck}`, {});
  }

  /**
   * Get supplier form
   * @param supplier
   * @returns FormGroup
   */
  public getUserForm(user: any = {}, id: string): FormGroup {
    return this._fb.group({
      organizationCode: [_.get(user, 'organizationCode', null), [Validators.required], this._customValitor.validateOrganizationCode.bind(this._customValitor.self)],
      firstName: [_.get(user, 'firstName', null), [Validators.required]],
      lastName: [_.get(user, 'lastName', null), [Validators.required]],
      phone: [_.get(user, 'phone', null), [Validators.pattern(RegexEnum.PHONE)]],
      email: [_.get(user, 'email', null), [Validators.required, Validators.pattern(RegexEnum.EMAIL)], this._customValitor.validateEmail.bind(this._customValitor.self)],
      roleCode: [_.get(user, 'roleCode', null), [Validators.required]],
      batchId: [this._batchId],
      id: id
    });
  }
}
